"use client";

import { useEffect, useRef, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const stats = [{
  prefix: "[ PROJ ]",
  value: 150,
  suffix: "+",
  descriptor: "SUCCESSFUL PROJECTS",
  accent: false
}, {
  prefix: "[ EXP ]",
  value: 8,
  suffix: "",
  descriptor: "YEARS OF CREATIVITY",
  accent: false
}, {
  prefix: "[ AWRD ]",
  value: 25,
  suffix: "",
  descriptor: "GLOBAL DESIGN AWARDS",
  accent: true
}, {
  prefix: "[ SAT ]",
  value: 100,
  suffix: "%",
  descriptor: "CLIENT SATISFACTION",
  accent: false
}];
const locations = ["MILAN", "PARIS", "LONDON", "TOKYO", "NEW YORK", "BERLIN"];
function CountUp({
  end,
  isVisible,
  duration = 1200,
  decimals = 0
}: {
  end: number;
  isVisible: boolean;
  duration?: number;
  decimals?: number;
}) {
  const [value, setValue] = useState(0);
  const rafRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isVisible) return;
    const startTime = performance.now();
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(end * eased);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setValue(end);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [end, duration, isVisible]);
  return <>{value.toFixed(decimals)}</>;
}
export default function Impactmetrics() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="3538" id="impactmetrics" className="bg-background text-foreground relative overflow-hidden">
      <div className="relative bg-slate-50 text-slate-900">
        <div ref={ref} className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 pt-20 md:pt-28 pb-16 md:pb-24">
          {}
          <div className="flex flex-col gap-16 md:gap-24">
            <div className="flex items-start justify-between gap-6">
              <span className="font-default text-xs tracking-widest text-slate-900">
                [ 04 / IMPACT ]
              </span>
              <span className="font-default text-xs tracking-widest text-slate-900 hidden md:inline">
                [ MARINELA PORTFOLIO ]
              </span>
            </div>

            <div>
              <h2 className="font-default italic text-2xl md:text-3xl text-slate-900" style={{
              letterSpacing: "-0.02em"
            }}>
                Measurable excellence.
              </h2>
            </div>
          </div>

          {}
          <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
            {stats.map((stat, i) => {
            const decimals = stat.value % 1 !== 0 ? 1 : 0;
            const isAccent = stat.accent;
            return <div key={i} data-index={i} className={["relative flex flex-col justify-between py-10 lg:py-6 px-0 md:px-8 lg:px-10", i > 0 ? "border-t md:border-t-0 md:border-l border-slate-900/10" : "", "transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"].join(" ")} style={{
              transitionDelay: `${i * 120}ms`
            }}>
                  <div className="font-default text-[10px] md:text-xs tracking-widest text-slate-900 mb-4">
                    {stat.prefix}
                  </div>

                  <div className="flex items-start font-default font-bold leading-[0.85]" style={{
                fontSize: "clamp(6rem, 12vw, 11rem)",
                letterSpacing: "-0.06em",
                color: isAccent ? "hsl(47 91% 53%)" : "hsl(0 0% 4%)",
                textShadow: isAccent ? "0 24px 48px hsla(47, 91%, 53%, 0.35), 0 8px 16px hsla(0, 0%, 0%, 0.15)" : "0 24px 48px hsla(0, 0%, 0%, 0.25), 0 8px 16px hsla(0, 0%, 0%, 0.12)"
              }}>
                    <span className="tabular-nums">
                      <CountUp end={stat.value} isVisible={isVisible} decimals={decimals} />
                    </span>
                    {stat.suffix && <span className="ml-1 self-start mt-2 text-[0.4em] font-medium">
                        {stat.suffix}
                      </span>}
                  </div>

                  <div className="mt-6 font-default text-base md:text-[16px] leading-snug text-slate-900" style={{
                letterSpacing: "-0.01em"
              }}>
                    {stat.descriptor}
                  </div>
                </div>;
          })}
          </div>
        </div>

        {}
        <div className="border-t border-slate-900/10 overflow-hidden">
          <div className="relative flex whitespace-nowrap py-4 ticker-track">
            {[...Array(3)].map((_, dup) => <div key={dup} data-index={dup} className="flex items-center shrink-0">
                {locations.map((loc, j) => <div key={`${dup}-${j}`} data-index={j} className="flex items-center px-8 font-default text-xs tracking-widest text-slate-900">
                    <span>[ {loc} ]</span>
                  </div>)}
              </div>)}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes glow-ticker-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
        .ticker-track {
          animation: glow-ticker-scroll 40s linear infinite;
          will-change: transform;
        }
      `}</style>
    </section>;
}